import React, { useEffect, useRef, useState } from "react";
import { gql } from "@apollo/client";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Input } from "@src/lib/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import MsgClock1Icon from "@src/lib/icons/18px/msg-clock-1";
import Pen2OutlineIcon from "@src/lib/icons/12px/pen-2-outline";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import PaginatedList from "@src/features/library/PaginatedList";
import { cn } from "@src/lib/utils/cn";
import { getFormattedDistanceToNow } from "@src/features/libraryItem/course/utils/utils";
import {
  OpusAgentChatTitleSource,
  OpusAgentChatsInput,
} from "@src/types.generated";
import {
  OpusAgentChatListItemFragment,
  useOpusAgentChatsQuery,
  useRenameOpusAgentChatMutation,
} from "./OpusAgentChatHistoryButton.generated";

export const UNTITLED_CHAT_LABEL = "Untitled chat";
// Mirrors scadmin's TITLE_MAX_LENGTH (opus_agent/controllers/titles.py),
// which rename_opus_agent_chat enforces with a bare ValueError — a codeless
// GraphQL error to the client — so the input pre-validates instead.
export const CHAT_TITLE_MAX_LENGTH = 255;
// Show the remaining-characters counter only this close to the cap.
const TITLE_COUNTER_THRESHOLD = 20;

// cache-and-network so reopening the popover refreshes titles/lastActivity
// (the AI titling pass lands after a turn finishes); cache-first afterwards
// keeps fetchMore pagination from refetching page one.
const useChatsListQuery: typeof useOpusAgentChatsQuery = (options) =>
  useOpusAgentChatsQuery({
    ...options,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

interface ChatRowProps {
  chat: OpusAgentChatListItemFragment;
  isActive: boolean;
  onSelect: () => void;
}

// One chat in the history list: click selects, the hover pencil renames
// inline. Renames land optimistically (MANUAL pins the title against the
// background AI titling pass); the mutation payload carries the
// authoritative chat and settles the cache by normalization.
const ChatRow: React.FC<ChatRowProps> = ({ chat, isActive, onSelect }) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  // Closing the editor (Enter, Escape, or the state flip itself) unmounts a
  // focused input, and browsers fire its blur while it leaves the DOM. This
  // flag settles the rename FIRST, so that trailing blur can neither commit
  // an escaped draft nor double-submit an entered one.
  const renameSettledRef = useRef(false);
  // A rename settled from the keyboard (Enter / Escape) hands focus back to
  // the pencil; otherwise the popover's focus trap drops it on the container.
  const renameButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  useEffect(() => {
    if (isRenaming || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    renameButtonRef.current?.focus();
  }, [isRenaming]);
  const [renameOpusAgentChat] = useRenameOpusAgentChatMutation();

  const startRenaming = (event: React.MouseEvent) => {
    event.stopPropagation();
    renameSettledRef.current = false;
    setDraft(chat.title ?? "");
    setIsRenaming(true);
  };

  const cancelRename = () => {
    renameSettledRef.current = true;
    setIsRenaming(false);
  };

  // Enter with an empty/whitespace draft is ignored (the editor stays open);
  // blur with one cancels. Either way nothing empty reaches the server.
  const submitRename = ({ viaBlur }: { viaBlur: boolean }) => {
    if (renameSettledRef.current) return;
    const title = draft.trim().slice(0, CHAT_TITLE_MAX_LENGTH);
    if (!title && !viaBlur) return;
    renameSettledRef.current = true;
    setIsRenaming(false);
    if (!title || title === chat.title) return;
    renameOpusAgentChat({
      variables: { id: chat.id, title },
      optimisticResponse: {
        renameOpusAgentChat: {
          __typename: "RenameOpusAgentChatResult",
          success: true,
          error: null,
          chat: {
            ...chat,
            title,
            titleSource: OpusAgentChatTitleSource.Manual,
          },
        },
      },
    }).catch(() => undefined);
  };

  if (isRenaming) {
    const remaining = CHAT_TITLE_MAX_LENGTH - draft.length;
    return (
      <div>
        <Input
          value={draft}
          maxLength={CHAT_TITLE_MAX_LENGTH}
          onChange={(event) =>
            setDraft(event.target.value.slice(0, CHAT_TITLE_MAX_LENGTH))
          }
          onBlur={() => submitRename({ viaBlur: true })}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              restoreFocusRef.current = !!draft.trim();
              submitRename({ viaBlur: false });
            }
            if (event.key === "Escape") {
              restoreFocusRef.current = true;
              cancelRename();
            }
          }}
          autoFocus
          data-opus-agent-rename-input=""
          data-testid="opus-agent-chat-rename-input"
          // A field in a p-1 list: h-8 like the rows, rounded-md (nested radius).
          inputClassName="h-8 rounded-md px-2"
        />
        {remaining <= TITLE_COUNTER_THRESHOLD && (
          <Text
            variant="fine-print-title"
            className="mt-0.5 block text-right text-text-tertiary"
            data-testid="opus-agent-chat-rename-counter"
          >
            {draft.length}/{CHAT_TITLE_MAX_LENGTH}
          </Text>
        )}
      </div>
    );
  }

  // The row is a real button (focusable, Enter/Space select) spanning the
  // title and the time; the rename pencil is its sibling, since buttons
  // cannot nest.
  return (
    <div
      className={cn(
        // A Popover option row (design.md › Popover, With list): rounded-md
        // in the p-1 list, state-hover on hover or keyboard focus, the
        // open chat on state-selected.
        "group flex items-center gap-1 rounded-md transition-colors duration-200 hover:bg-state-hover has-[button:focus-visible]:bg-state-hover",
        isActive && "bg-state-selected hover:bg-state-selected",
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? "true" : undefined}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 self-stretch rounded-md px-2 py-1.5 text-left outline-none"
        data-testid={`opus-agent-chat-row-${chat.id}`}
      >
        <Text
          variant="compact-description"
          ellipsis
          style={{ flex: 1, alignSelf: "stretch" }}
          className="text-left text-text-primary"
        >
          {chat.title || UNTITLED_CHAT_LABEL}
        </Text>
        <Text
          variant="helper-description"
          className="shrink-0 text-right text-text-tertiary"
        >
          {getFormattedDistanceToNow(
            new Date(chat.lastActivity ?? chat.createdAt),
          )}
        </Text>
      </button>
      <Button
        ref={renameButtonRef}
        variant="ghost"
        size="icon-xs"
        className="mr-1 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        onClick={startRenaming}
        aria-label="Rename chat"
        title="Rename chat"
        data-testid={`opus-agent-chat-rename-${chat.id}`}
      >
        <Pen2OutlineIcon className="text-icon-tertiary" />
      </Button>
    </div>
  );
};

export interface OpusAgentChatListProps {
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
  input?: OpusAgentChatsInput;
  className?: string;
}

export const OpusAgentChatList: React.FC<OpusAgentChatListProps> = ({
  activeChatId,
  onSelectChat,
  input = {},
  className,
}) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  return (
    <ScrollableFlexGrow
      flex="0 1 auto"
      className={className}
      innerRef={scrollableRef}
    >
      <PaginatedList<
        OpusAgentChatListItemFragment,
        typeof useOpusAgentChatsQuery
      >
        queryHook={useChatsListQuery}
        getTotalItemCount={(chatsData) =>
          chatsData?.OpusAgentChats?.totalCount ?? 0
        }
        getItems={(chatsData) => chatsData?.OpusAgentChats?.objects ?? []}
        itemId={(chat) => chat.id}
        input={input}
        className="flex flex-col gap-0.5 p-1"
        viewportRef={scrollableRef}
        renderItem={(chat) => (
          <ChatRow
            chat={chat}
            isActive={chat.id === activeChatId}
            onSelect={() => onSelectChat(chat.id)}
          />
        )}
        noDataEmptyState={
          <Text
            variant="helper-description"
            className="block px-2 py-1.5 text-text-tertiary"
          >
            No chat history
          </Text>
        }
        dataTestId="opus-agent-chat-history-list"
      />
    </ScrollableFlexGrow>
  );
};

export interface OpusAgentChatHistoryButtonProps {
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
  input?: OpusAgentChatsInput;
}

// The drawer-header affordance for the chat history: same chrome as the Ask
// Opus ChatHistoryIconButton (popover + paginated list), backed by
// OpusAgentChats (lastActivity ordering is the server default).
export const OpusAgentChatHistoryButton: React.FC<
  OpusAgentChatHistoryButtonProps
> = ({ activeChatId, onSelectChat, input = {} }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="View chat history"
              data-testid="opus-agent-chat-history-trigger"
            >
              <MsgClock1Icon />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">View chat history</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="end"
        // Escape in a rename input cancels the rename only; the popover's
        // document-level Escape handler would otherwise close it too.
        onEscapeKeyDown={(event) => {
          if (
            event.target instanceof HTMLElement &&
            event.target.closest("[data-opus-agent-rename-input]")
          )
            event.preventDefault();
        }}
        // A column, so on a short viewport the list shrinks to the popover's
        // available-height cap and scrolls instead of being clipped.
        className="flex max-h-125 w-80 flex-col p-0"
      >
        <div className="border-b border-border-primary px-3 py-2">
          <Text variant="compact-title">Chat history</Text>
        </div>
        <OpusAgentChatList
          className="h-80 min-h-0 shrink"
          activeChatId={activeChatId}
          onSelectChat={(chatId) => {
            onSelectChat(chatId);
            setOpen(false);
          }}
          input={input}
        />
      </PopoverContent>
    </Popover>
  );
};

export default OpusAgentChatHistoryButton;

gql`
  fragment OpusAgentChatListItem on OpusAgentChat {
    model
    id
    title
    titleSource
    lastActivity
    createdAt
  }

  query OpusAgentChats(
    $input: OpusAgentChatsInput!
    $pagination: PaginationInput
  ) {
    OpusAgentChats(input: $input, pagination: $pagination) {
      objects {
        ...OpusAgentChatListItem
      }
      totalCount
    }
  }

  mutation RenameOpusAgentChat($id: UUID!, $title: String!) {
    renameOpusAgentChat(id: $id, title: $title) {
      success
      error {
        code
        message
      }
      chat {
        ...OpusAgentChatListItem
      }
    }
  }
`;
